// Homepage product rain. One shared clock moves every pack, so packs can never drift out of step (independent CSS animations restart separately when the editor re-renders or the layout changes, which let packs collide). Each lane measures its visible packs and spaces them at least one pack height plus a gap apart, then loops them at the same speed. Without motion preference nothing runs and the CSS still layout shows.
const RAIN_PERIOD_MS = 21000;

class HeroRain extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.sky = this.querySelector('.hero-rain__sky');
    this.drops = [...this.querySelectorAll('.rain-drop[data-lane]')];
    this.lanes = [];
    this.elapsed = 0;
    this.last = null;
    this.frame = null;
    this.onScreen = true;
    this.paused = false;

    if (!this.sky || !this.drops.length) return;

    // Pause only while a pack has keyboard focus, so keyboard users can reach a pack; hovering never stops the rain.
    // Clicks and taps don't count (they leave focus on the link, which would freeze the rain after pressing Back), and returning to the page resets it.
    const pause = (event) => { if (event.target.matches?.(':focus-visible')) this.paused = true; };
    const resume = () => { if (!this.querySelector(':focus-visible')) { this.paused = false; this.last = null; } };
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', () => requestAnimationFrame(resume), { signal });
    window.addEventListener('pageshow', () => { document.activeElement?.closest?.('.rain-drop')?.blur(); this.paused = false; this.last = null; this.start(); }, { signal });

    this.motion = window.matchMedia('(prefers-reduced-motion: no-preference)');
    this.motion.addEventListener('change', () => this.update(), { signal });

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.sky);
    for (const drop of this.drops) this.resizeObserver.observe(drop);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.onScreen = Boolean(entry?.isIntersecting);
      this.last = null;
      if (this.onScreen) this.start();
      else this.stop();
    });
    this.intersectionObserver.observe(this);

    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.stop();
  }

  update() {
    if (this.motion?.matches) {
      this.setAttribute('data-js-rain', '');
      this.measure();
      this.start();
    } else {
      this.removeAttribute('data-js-rain');
      this.stop();
      for (const drop of this.drops) drop.style.translate = '';
    }
  }

  measure() {
    if (!this.sky) return;
    const skyHeight = this.sky.clientHeight;
    // data-gap lets a page ask for wider spacing between sweets (as a share of the sky height); the homepage keeps the default.
    const gap = Math.max(24, skyHeight * (parseFloat(this.dataset.gap) || 0.08));
    const groups = new Map();

    for (const drop of this.drops) {
      if (!drop.offsetWidth) continue; // hidden at this screen size
      const lane = drop.dataset.lane;
      if (!groups.has(lane)) groups.set(lane, []);
      groups.get(lane).push(drop);
    }

    const laneCount = groups.size || 1;
    this.lanes = [...groups.values()].map((drops, index) => {
      // Height plus room for the gentle tilt and sway rotation.
      const tallest = Math.max(...drops.map((drop) => drop.offsetHeight + drop.offsetWidth * 0.12));
      const length = Math.max(skyHeight + tallest, (tallest + gap) * drops.length);
      const step = length / drops.length;
      return { drops, tallest, length, step, shift: step * (index / laneCount) };
    });

    this.render();
  }

  start() {
    if (this.frame || !this.onScreen || !this.hasAttribute('data-js-rain')) return;
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.last = null;
  }

  tick = (now) => {
    if (this.last !== null && !this.paused) {
      this.elapsed += Math.min(now - this.last, 100);
    }
    this.last = now;
    this.render();
    this.frame = requestAnimationFrame(this.tick);
  };

  render() {
    const progress = this.elapsed / RAIN_PERIOD_MS;
    for (const lane of this.lanes) {
      lane.drops.forEach((drop, index) => {
        const position = (progress * lane.length + index * lane.step + lane.shift) % lane.length;
        drop.style.translate = `0 ${(position - lane.tallest).toFixed(1)}px`;
      });
    }
  }
}

if (!customElements.get('hero-rain')) customElements.define('hero-rain', HeroRain);
